/** @format */

import { END_POINT } from "utils";
import { getRequest } from "./axios";
import {
  ServiceRevenueQuery,
  ServiceRevenueResponse,
  ServiceRevenueSummaryResponse,
  S2aQuery,
  S2bQuery,
  S2cQuery,
  S2dQuery,
  S2eQuery,
  S3aQuery,
  S4Query,
  S5Query,
  S6Query,
  S7Query,
  TResponse,
} from "types";

const buildQueryString = (params: ServiceRevenueQuery) => {
  const search = new URLSearchParams();
  if (params.from_date) search.append("from_date", params.from_date);
  if (params.to_date) search.append("to_date", params.to_date);
  if (Array.isArray(params.group_ids) && params.group_ids.length) {
    params.group_ids.forEach((id) => search.append("group_ids[]", String(id)));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

const buildBasicQueryString = (params: {
  from_date: string;
  to_date: string;
  [key: string]: any;
}) => {
  const search = new URLSearchParams();
  if (params.from_date) search.append("from_date", params.from_date);
  if (params.to_date) search.append("to_date", params.to_date);

  // Handle optional filters
  Object.keys(params).forEach((key) => {
    if (
      key !== "from_date" &&
      key !== "to_date" &&
      params[key] !== undefined &&
      params[key] !== null
    ) {
      if (Array.isArray(params[key]) && params[key].length > 0) {
        params[key].forEach((val: any) =>
          search.append(`${key}[]`, String(val)),
        );
      } else if (!Array.isArray(params[key])) {
        search.append(key, String(params[key]));
      }
    }
  });

  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const reportService = {
  s1aHkd: async (
    params: ServiceRevenueQuery,
  ): Promise<ServiceRevenueResponse | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S1A_HKD}${buildQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },
  s1aHkdSummary: async (
    params: ServiceRevenueQuery,
  ): Promise<ServiceRevenueSummaryResponse | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S1A_HKD}/summary${buildQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s2aHkd: async (params: S2aQuery): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S2A_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s2bHkd: async (params: S2bQuery): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S2B_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s2cHkd: async (params: S2cQuery): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S2C_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s2dHkd: async (params: S2dQuery): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S2D_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s2eHkd: async (params: S2eQuery): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S2E_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s3aHkd: async (params: S3aQuery): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S3A_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s4Hkd: async (params: S4Query): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S4_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s5Hkd: async (params: S5Query): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S5_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s6Hkd: async (params: S6Query): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S6_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  s7Hkd: async (params: S7Query): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(
        `${END_POINT.REPORTS.S7_HKD}${buildBasicQueryString(params)}`,
      );
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  // ============================================================
  // Admin Reports — Sổ sách, báo cáo quản trị
  // ============================================================

  rpInOut: async (params: { from_date: string; to_date: string; wms_id?: number; group_id?: number; show_inactive?: boolean }): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(`${END_POINT.REPORTS.RP_IN_OUT}${buildBasicQueryString(params)}`);
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  rpAccountReceivable: async (params: { from_date: string; to_date: string; type?: string; partner_id?: number }): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(`${END_POINT.REPORTS.RP_ACCOUNT_RECEIVABLE}${buildBasicQueryString(params)}`);
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  rpAccountReceivableDetail: async (params: { from_date: string; to_date: string; type?: string; partner_id?: number }): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(`${END_POINT.REPORTS.RP_ACCOUNT_RECEIVABLE_DETAIL}${buildBasicQueryString(params)}`);
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  rpDownGtgt: async (params: { from_date: string; to_date: string }): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(`${END_POINT.REPORTS.RP_DOWN_GTGT}${buildBasicQueryString(params)}`);
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  rpHkdState: async (params: { from_date: string; to_date: string; data_method?: string }): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(`${END_POINT.REPORTS.RP_HKD_STATE}${buildBasicQueryString(params)}`);
      return rs.data;
    } catch (error) {
      return null;
    }
  },

  rpPurchaseDetail: async (params: { from_date: string; to_date: string; partner_id?: number; product_id?: number }): Promise<TResponse<any> | null> => {
    try {
      const rs = await getRequest(`${END_POINT.REPORTS.RP_PURCHASE_DETAIL}${buildBasicQueryString(params)}`);
      return rs.data;
    } catch (error) {
      return null;
    }
  },
};
